import { useEffect, useRef, useState } from 'react'
import type { Locality } from '../api'

/* Public Mapbox token, baked in at build time. Browser-safe by design — it
 * signs nothing and buys nothing but raster tiles — but it must be a
 * URL-restricted token, never a secret one. Absent, the map does not render
 * and the picker falls back to typed coordinates, which is a degradation
 * rather than a failure. */
const TOKEN = import.meta.env.VITE_MAPBOX_TOKEN as string | undefined

export interface Pin { lat: number; lon: number }

/** Satellite map for choosing where a deployment went.
 *
 *  MapLibre with Mapbox raster tiles, the same combination phoniq-map and
 *  phoniq-insight already use — so the basemap looks like the rest of the
 *  estate and there is one token to rotate rather than two stacks to keep in
 *  step. Satellite rather than a street style because the subject is water:
 *  a farm reads as a visible ring of cages, and a street map of a fjord is an
 *  empty blue shape.
 *
 *  Imported dynamically, like hls.js in live.ts. MapLibre is roughly twice the
 *  size of everything else here put together, and it is needed on one step of
 *  one flow — loading it for someone who opened the fleet list to check a
 *  level would be the whole bundle paying for a page they never see.
 */
export function DeployMap({
  pin, farms, fit, onPick, onPickFarm,
}: {
  pin: Pin | null
  /** Drawn as circles so a farm can be clicked instead of aimed at. */
  farms?: Locality[]
  /** Points to bring into view when they change — normally the current search
   *  hits, or the farm just chosen.
   *
   *  Deliberately separate from `pin`. Framing the map and choosing a position
   *  are different acts, and folding them together would mean a search moved
   *  the pin: someone who has already placed the unit, then searches again to
   *  check a neighbouring farm's name, would silently lose the position they
   *  set. So this only ever changes the camera. */
  fit?: Pin[]
  onPick: (p: Pin) => void
  onPickFarm?: (l: Locality) => void
}) {
  const el = useRef<HTMLDivElement>(null)
  const map = useRef<any>(null)
  const marker = useRef<any>(null)
  const lib = useRef<any>(null)
  const [failed, setFailed] = useState<string | null>(null)

  // The map is built by an async import, so every effect below runs at least
  // once while `map.current` is still null and bails. Nothing used to bring
  // them back: a pin or a camera target that was already set at mount was
  // simply never applied. This bumps once the map exists, and is a dependency
  // of each of those effects, so they run again against a real map.
  const [ready, setReady] = useState(0)

  // Callbacks live in refs so the map is built once. Rebuilding it on every
  // keystroke in the search box would tear down the WebGL context and lose
  // whatever the operator had panned to.
  const onPickRef = useRef(onPick)
  const onPickFarmRef = useRef(onPickFarm)
  onPickRef.current = onPick
  onPickFarmRef.current = onPickFarm

  useEffect(() => {
    if (!TOKEN) { setFailed('no-token'); return }
    let dead = false
    let m: any

    void (async () => {
      try {
        const maplibregl = (await import('maplibre-gl')).default
        await import('maplibre-gl/dist/maplibre-gl.css')
        if (dead || !el.current) return
        lib.current = maplibregl

        m = new maplibregl.Map({
          container: el.current,
          attributionControl: false,
          style: {
            version: 8,
            sources: {
              satellite: {
                type: 'raster',
                tiles: [`https://api.tiles.mapbox.com/v4/mapbox.satellite/{z}/{x}/{y}.jpg?access_token=${TOKEN}`],
                tileSize: 256,
                attribution: '© Mapbox · © OpenStreetMap',
              },
            },
            layers: [{ id: 'satellite', type: 'raster', source: 'satellite' }],
          },
          // Norway, wide enough to show the whole coast before anyone has said
          // where they are.
          center: [13.5, 65.5],
          zoom: 4.2,
        })
        map.current = m
        setReady((n) => n + 1)
        m.addControl(new maplibregl.NavigationControl({ showCompass: false }), 'top-right')
        m.addControl(new maplibregl.AttributionControl({ compact: true }), 'bottom-right')

        m.on('click', (e: any) => {
          // A farm click is handled by its own layer handler, which stops
          // propagation. Anything else is a free position.
          onPickRef.current({ lat: e.lngLat.lat, lon: e.lngLat.lng })
        })
        m.on('error', (e: any) => {
          // Tiles failing is the one that matters and it is silent otherwise:
          // a blocked CSP or a rejected token leaves a black rectangle that
          // looks like an empty sea.
          if (e?.error?.status === 401 || e?.error?.status === 403) setFailed('token')
        })
      } catch {
        if (!dead) setFailed('load')
      }
    })()

    return () => { dead = true; try { m?.remove() } catch { /* already gone */ } }
  }, [])

  // The pin, kept in step with whatever the wizard currently holds — including
  // a farm chosen from the search list rather than from the map.
  useEffect(() => {
    const m = map.current, maplibregl = lib.current
    if (!m || !maplibregl) return
    if (!pin) { marker.current?.remove(); marker.current = null; return }
    if (!marker.current) {
      // The brand's signal dot, not MapLibre's default teardrop. Red is the
      // one colour this estate spends on "here is the thing itself", and the
      // same 8px mark already means it in the wordmark and on a live unit —
      // so a deployment reads as the same object on the map as in the header.
      //
      // A dot also anchors honestly. A teardrop points at its position from
      // above and covers it; a dot sits on it, which matters when the whole
      // task is saying which corner of a cage ring the hydrophone hung off.
      const dot = document.createElement('div')
      dot.className = 'map-pin'
      dot.setAttribute('aria-label', 'Deployment position')
      dot.append(document.createElement('i'))
      marker.current = new maplibregl.Marker({ element: dot, draggable: true })
      marker.current.on('dragend', () => {
        const p = marker.current.getLngLat()
        onPickRef.current({ lat: p.lat, lon: p.lng })
      })
      marker.current.setLngLat([pin.lon, pin.lat]).addTo(m)
    } else {
      marker.current.setLngLat([pin.lon, pin.lat])
    }
    // Only fly when the pin is off-screen, so nudging a marker does not yank
    // the view out from under the hand doing the nudging.
    if (!m.getBounds().contains([pin.lon, pin.lat])) {
      m.flyTo({ center: [pin.lon, pin.lat], zoom: Math.max(m.getZoom(), 11), speed: 1.4 })
    }
  }, [pin?.lat, pin?.lon, ready])

  // Bring `fit` into view. Keyed on the rounded coordinates rather than the
  // array identity, so a parent that rebuilds the list on every render does
  // not re-fly the camera continuously.
  const fitKey = (fit ?? []).map((p) => `${p.lat.toFixed(4)},${p.lon.toFixed(4)}`).join('|')
  useEffect(() => {
    const m = map.current, maplibregl = lib.current
    if (!m || !maplibregl || !fit || fit.length === 0) return
    const pts = fit
    const go = () => {
      const only = pts.length === 1 ? pts[0] : null
      if (only) {
        // One hit is a place, not an extent. fitBounds on a zero-area box
        // zooms to the maximum, which on satellite tiles is a featureless
        // crop of sea; 13 shows the farm and enough water around it to see
        // where the mooring would be.
        m.flyTo({ center: [only.lon, only.lat], zoom: 13, speed: 1.6 })
        return
      }
      const b = new maplibregl.LngLatBounds()
      for (const p of pts) b.extend([p.lon, p.lat])
      m.fitBounds(b, { padding: 60, maxZoom: 13, duration: 900 })
    }
    if (m.isStyleLoaded()) go()
    else m.once('load', go)
  }, [fitKey, ready])

  // Farms as a clickable layer. A GeoJSON source rather than a marker each:
  // 1778 DOM nodes is a different kind of slow from 1778 points in one layer.
  useEffect(() => {
    const m = map.current
    if (!m || !farms) return
    const data = {
      type: 'FeatureCollection',
      features: farms.map((f) => ({
        type: 'Feature',
        geometry: { type: 'Point', coordinates: [f.lon, f.lat] },
        properties: { locality_no: f.locality_no, name: f.name },
      })),
    }
    const apply = () => {
      const src = m.getSource('farms')
      if (src) { src.setData(data); return }
      m.addSource('farms', { type: 'geojson', data })
      m.addLayer({
        id: 'farms',
        type: 'circle',
        source: 'farms',
        paint: {
          'circle-radius': ['interpolate', ['linear'], ['zoom'], 4, 3, 10, 7],
          'circle-color': '#E8843C',
          'circle-stroke-color': '#0b0f14',
          'circle-stroke-width': 1.2,
          'circle-opacity': 0.9,
        },
      })
      m.on('click', 'farms', (e: any) => {
        e.originalEvent?.stopPropagation()
        const p = e.features?.[0]?.properties
        const hit = farmsRef.current.find(
          (f) => f.locality_no === Number(p?.locality_no))
        if (hit && onPickFarmRef.current) onPickFarmRef.current(hit)
      })
      m.on('mouseenter', 'farms', () => { m.getCanvas().style.cursor = 'pointer' })
      m.on('mouseleave', 'farms', () => { m.getCanvas().style.cursor = '' })
    }
    if (m.isStyleLoaded()) apply()
    else m.once('load', apply)
  }, [farms, ready])

  // Read inside the click handler, which is registered once and would
  // otherwise close over the first render's list for ever.
  const farmsRef = useRef<Locality[]>([])
  farmsRef.current = farms ?? []

  if (failed) {
    return (
      <div className="mapslot">
        <div>
          <strong>
            {failed === 'no-token'
              ? 'Map unavailable — no Mapbox token in this build'
              : failed === 'token'
                ? 'Map unavailable — the Mapbox token was rejected'
                : 'Map could not be loaded'}
          </strong>
          <span>
            Type coordinates below instead. Everything else about the
            deployment works exactly the same.
          </span>
        </div>
      </div>
    )
  }

  return (
    <div className="mapwrap">
      <div ref={el} className="mapcanvas" />
      <div className="maphint">
        Click the map to set the position, or drag the red marker. Orange
        circles are registered farms — click one to pick it.
      </div>
    </div>
  )
}
